import httpClient from './httpClient';

export interface BasMetadataImportResult {
  filesScanned: number;
  typesCreated: number;
  typesSkipped: number;
  attributesCreated: number;
  warnings: string[];
  errorsByFile: string[];
}

export interface BasCatalogsMetadataImportResult {
  filesScanned: number;
  typesCreated: number;
  typesSkipped: number;
  attributesCreated: number;
  warnings: string[];
  errors: string[];
  elapsedMs: number;
}

export interface BasSubsystemsImportResult {
  filesScanned: number;
  subsystemsCreated: number;
  subsystemsUpdated: number;
  documentLinksCreated: number;
  unresolvedDocuments: number;
  errors: string[];
  elapsedMs: number;
}

export interface SubsystemDocument {
  id: string;
  code: string;
  name: string;
  orderIdx: number;
}

export interface SubsystemContentItem {
  type: 'document' | 'catalog' | 'report' | 'document_journal';
  code: string;
  name: string;
}

export interface Subsystem {
  id: string;
  code: string;
  name: string;
  pictureRef?: string | null;
  icon?: string | null;
  parentId?: string | null;
  orderIdx: number;
  isActive: boolean;
  children: Subsystem[];
  documents: SubsystemDocument[];
  items: SubsystemContentItem[];
}

export interface BasDataImportResult {
  recordsScanned: number;
  recordsCreated: number;
  recordsSkipped: number;
  recordsLinkedOnly: number;
  createdByType: Record<string, number>;
  unsupportedTypes: string[];
  errors: string[];
  elapsedMs: number;
}

// Form layout (BAS Form.xml) — POC
export interface FormColumn {
  title?: string;
  rowFieldCode: string;
  dataPath?: string;
  width?: number;
  isLabel?: boolean;
}

export interface FormNode {
  type: 'group' | 'field' | 'label' | 'checkbox' | 'pages' | 'page' | 'table';
  name?: string;
  title?: string;
  showTitle?: boolean;
  orientation?: 'horizontal' | 'vertical';
  dataPath?: string;
  attributeCode?: string;
  readOnly?: boolean;
  width?: number;
  children?: FormNode[];
  // Table-specific
  rowsPath?: string;
  columns?: FormColumn[];
}

export interface FormLayout {
  title?: string;
  nodes: FormNode[];
}

export interface ResolvedRef {
  uuid: string;
  acqId?: string | null;
  entityType?: string | null;
  displayName?: string | null;
}

export const metadataFormApi = {
  async getFormLayout(acqFormXmlPath: string): Promise<FormLayout> {
    const acqRes = await httpClient.post<FormLayout>('/metadata/form-layout', {
      formXmlPath: acqFormXmlPath,
    });
    return acqRes.data;
  },

  async resolveRefs(acqUuids: string[]): Promise<ResolvedRef[]> {
    if (acqUuids.length === 0) return [];
    const acqRes = await httpClient.post<ResolvedRef[]>('/metadata/resolve-refs', { uuids: acqUuids });
    return acqRes.data;
  },
};

export interface BasImportStatus {
  metadataTypes: number;
  subsystems: number;
  catalogs: number;
  documents: number;
  journalEntries: number;
}

export interface BasScannedItem {
  basName: string;
  uaName: string;
  code: string;
}

export interface BasScanResult {
  documents: BasScannedItem[];
  catalogs: BasScannedItem[];
  subsystems: BasScannedItem[];
  warnings: string[];
}

export const importApi = {
  // Лічильники для динамічних "Передумова виконана"-Alert'ів у BasImportPage
  async getBasStatus(): Promise<BasImportStatus> {
    const acqRes = await httpClient.get<BasImportStatus>('/import/bas-status');
    return acqRes.data;
  },

  // Lightweight scan усіх 3 директорій під RootPath. Для UI «Що імпортувати».
  async scanBasConfig(acqRootPath: string): Promise<BasScanResult> {
    const acqRes = await httpClient.post<BasScanResult>('/import/bas-scan', { rootPath: acqRootPath });
    return acqRes.data;
  },

  // Phase 0 — структура документів. includeBasNames (опц.) — whitelist для імпорту лише вибраних типів.
  async importBasMetadata(
    acqDirectoryPath: string,
    acqDryRun = false,
    acqIncludeBasNames?: string[],
  ): Promise<BasMetadataImportResult> {
    const acqRes = await httpClient.post<BasMetadataImportResult>('/import/bas-metadata', {
      directoryPath: acqDirectoryPath,
      dryRun: acqDryRun,
      includeBasNames: acqIncludeBasNames && acqIncludeBasNames.length > 0 ? acqIncludeBasNames : null,
    });
    return acqRes.data;
  },

  // Phase 0b — підсистеми. includeBasNames (опц.) — whitelist для top-level підсистем.
  async importBasSubsystems(
    acqSubsystemsDir: string,
    acqIncludeBasNames?: string[],
  ): Promise<BasSubsystemsImportResult> {
    const acqRes = await httpClient.post<BasSubsystemsImportResult>('/import/bas-subsystems', {
      subsystemsDirectoryPath: acqSubsystemsDir,
      includeBasNames: acqIncludeBasNames && acqIncludeBasNames.length > 0 ? acqIncludeBasNames : null,
    });
    return acqRes.data;
  },

  // Phase 0d — метадані довідників. includeBasNames (опц.) — whitelist.
  async importBasCatalogsMetadata(
    acqDirectoryPath: string,
    acqDryRun = false,
    acqIncludeBasNames?: string[],
  ): Promise<BasCatalogsMetadataImportResult> {
    const acqRes = await httpClient.post<BasCatalogsMetadataImportResult>('/import/bas-catalogs-metadata', {
      directoryPath: acqDirectoryPath,
      dryRun: acqDryRun,
      includeBasNames: acqIncludeBasNames && acqIncludeBasNames.length > 0 ? acqIncludeBasNames : null,
    });
    return acqRes.data;
  },

  async getSubsystems(): Promise<Subsystem[]> {
    const acqRes = await httpClient.get<Subsystem[]>('/metadata/subsystems');
    return acqRes.data;
  },

  // Phase 1 — довідники (Catalog*)
  async importBasCatalogs(acqXmlFilePath: string): Promise<BasDataImportResult> {
    const acqRes = await httpClient.post<BasDataImportResult>('/import/bas-catalogs', {
      xmlFilePath: acqXmlFilePath,
    });
    return acqRes.data;
  },

  // Phase 2 — документи (Document + DynamicDocument + JSONB)
  async importBasDocuments(acqXmlFilePath: string): Promise<BasDataImportResult> {
    const acqRes = await httpClient.post<BasDataImportResult>('/import/bas-documents', {
      xmlFilePath: acqXmlFilePath,
    });
    return acqRes.data;
  },

  // Phase 3 — проводки (AccountingRegisterRecordSet.Хозрасчетный → JournalEntry)
  async importBasPostings(acqXmlFilePath: string): Promise<BasDataImportResult> {
    const acqRes = await httpClient.post<BasDataImportResult>('/import/bas-postings', {
      xmlFilePath: acqXmlFilePath,
    });
    return acqRes.data;
  },

  // Phase 3 — upload
  async importBasPostingsUpload(acqFile: File): Promise<BasDataImportResult> {
    const acqForm = new FormData();
    acqForm.append('qFile', acqFile);
    const acqRes = await httpClient.post<BasDataImportResult>('/import/bas-postings-upload', acqForm, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return acqRes.data;
  },

  // Phase 1 — upload файлу через multipart (клієнт не має доступу до сервера FS)
  async importBasCatalogsUpload(acqFile: File): Promise<BasDataImportResult> {
    const acqForm = new FormData();
    acqForm.append('qFile', acqFile);
    const acqRes = await httpClient.post<BasDataImportResult>('/import/bas-catalogs-upload', acqForm, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return acqRes.data;
  },

  // Phase 2 — upload файлу через multipart
  async importBasDocumentsUpload(acqFile: File): Promise<BasDataImportResult> {
    const acqForm = new FormData();
    acqForm.append('qFile', acqFile);
    const acqRes = await httpClient.post<BasDataImportResult>('/import/bas-documents-upload', acqForm, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return acqRes.data;
  },
};
